import { getInitialSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import {
  getSvdVariantsForOptions,
  getUpdatedSvdSelection,
} from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import { getSvdPriceDetails } from '@src/quince-site-common/src/helpers/svd-price-helper'
import { getVarientId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { cloneDeep } from '@src/utils/functions/utils'

import {
  addPurchaseInfoToProductConfigs,
  carryOverSvdSelection,
  getProductInitialState,
} from './BundleProductPurchase.helpers'
import {
  BUNDLE_PRODUCT_TYPE,
  BundleAction,
  BundleActionType,
  BundleConfig,
  BundleProductConfig,
} from './BundleProductPurchase.types'

const OOS_ERROR_MESSAGE = 'Out of stock'
const CONFIG_OPTIONAL_PRODUCTS_KEY = 'optionalProducts'
const CONFIG_PRODUCTS_KEY = 'products'

export default function bundleReducer(state: BundleConfig, action: BundleAction): BundleConfig {
  const { type, payload } = action

  switch (type) {
    case BundleActionType.UPDATE_SELECTED_OPTIONS: {
      const { index, optionName, optionValue, isOptional } = payload
      const productsToUpdate = isOptional ? state.optionalProducts : state.products
      const product = productsToUpdate[index]?.product
      const selectedOptions = productsToUpdate[index]?.selectedOptions
      const purchaseInfo = productsToUpdate[index]?.purchaseInfo
      const updatedSelectedOptions = productHelper.getUpdatedSelectedOptionsOnProductOptionSelect(
        product,
        selectedOptions,
        optionName,
        optionValue,
      )
      const variants = productHelper.getVariantsForOptions(product, updatedSelectedOptions)
      const pricing = productHelper.getProductPriceDetails(product, updatedSelectedOptions)
      const isOutOfStock =
        variants.length === 1 && (purchaseInfo?.svidVsSvDetails[getVarientId(variants[0])]?.quantityAvailable || 0) <= 0
      const updatedProductConfig: BundleProductConfig = {
        ...productsToUpdate[index],
        variants: variants,
        selectedOptions: updatedSelectedOptions,
        error: isOutOfStock ? OOS_ERROR_MESSAGE : null,
        salePrice: +pricing.formattedSalePrice,
        traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
      }
      const updatedConfig: BundleConfig = cloneDeep(state)
      updatedConfig[isOptional ? CONFIG_OPTIONAL_PRODUCTS_KEY : CONFIG_PRODUCTS_KEY][index] = updatedProductConfig
      return updatedConfig
    }

    // SVD only (BUNDLE_PURCHASE_V2). A legacy bundle never dispatches this, and the `isSvd` guard makes it
    // a no-op if one somehow did.
    //
    // Deliberately does NOT `cloneDeep` the state like the legacy actions do: an SVD bundle carries the full
    // variant set per constituent (the sample bundle is 562 variants across 6 products, each with its own
    // attribute map), so a deep clone on every swatch click would be a visible cost. A shallow-immutable
    // replace of the one changed slot gives the reducer the same new-reference semantics for free.
    case BundleActionType.UPDATE_SVD_SELECTED: {
      const { index, vdaKey, valueKey, isOptional } = payload
      const listKey = isOptional ? CONFIG_OPTIONAL_PRODUCTS_KEY : CONFIG_PRODUCTS_KEY
      const configs = state[listKey]
      const config = configs[index]
      if (!config?.isSvd) return state

      const svdSelected = getUpdatedSvdSelection(config.product, config.svdSelected ?? [], vdaKey, valueKey)
      const variants = getSvdVariantsForOptions(config.product, svdSelected)
      // The server-resolved variant is stale the moment the shopper picks anything — from here the slot
      // resolves min-price, matching the single-product V2 buy box.
      const pricing = getSvdPriceDetails(config.product, svdSelected, undefined)
      const isOutOfStock =
        variants.length === 1 &&
        (config.purchaseInfo?.svidVsSvDetails[getVarientId(variants[0])]?.quantityAvailable || 0) <= 0

      const updatedProductConfig: BundleProductConfig = {
        ...config,
        svdSelected,
        serverVariantId: undefined,
        variants,
        error: isOutOfStock ? OOS_ERROR_MESSAGE : null,
        salePrice: +pricing.formattedSalePrice,
        traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
      }

      return {
        ...state,
        [listKey]: configs.map((entry, entryIndex) => (entryIndex === index ? updatedProductConfig : entry)),
      }
    }

    case BundleActionType.UPDATE_SELECTION: {
      const { index, checked } = payload
      const updatedConfig: BundleConfig = cloneDeep(state)
      if (updatedConfig.optionalProducts[index]) {
        updatedConfig.optionalProducts[index].selection.isSelected = checked
      }
      return updatedConfig
    }

    case BundleActionType.UPDATE_SIBLING_SELECTED_OPTIONS: {
      const { index, product: newSiblingProduct, isOptional, purchaseInfo, searchParams } = payload
      const products = isOptional ? state.optionalProducts : state.products
      const productToReplace = products[index]
      if (!productToReplace) {
        console.error('Unable to find product details for the current sibling product')
      }
      const prevProductSelection = productToReplace?.selection

      // SVD slot: re-seed the sibling from its OWN cascade, carrying over whatever the shopper had picked
      // that the sibling also offers (`carryOverSvdSelection`). The legacy option carry-over below is
      // skipped entirely — an SVD product has no `productOptions` to seed from.
      if (productToReplace?.isSvd) {
        const svdSelected = carryOverSvdSelection(newSiblingProduct, productToReplace.svdSelected)
        const svdVariants = getSvdVariantsForOptions(newSiblingProduct, svdSelected)
        const svdPricing = getSvdPriceDetails(newSiblingProduct, svdSelected, undefined)
        const isSiblingOutOfStock =
          svdVariants.length === 1 &&
          (purchaseInfo?.svidVsSvDetails[getVarientId(svdVariants[0])]?.quantityAvailable || 0) <= 0

        const updatedSvdConfig: BundleProductConfig = {
          ...productToReplace,
          product: newSiblingProduct,
          svdSelected,
          // The swapped-in sibling has no server-resolved variant of its own here — min-price takes over.
          serverVariantId: undefined,
          variants: svdVariants,
          purchaseInfo,
          error: isSiblingOutOfStock ? OOS_ERROR_MESSAGE : null,
          salePrice: +svdPricing.formattedSalePrice,
          traditionalPrice: +(svdPricing.formattedTraditionalPrice || '0'),
          // Add-on checked/unchecked state survives the swap (V1 parity); `productToReplace` is non-null
          // inside this branch, so the selection is always defined here.
          selection: productToReplace.selection,
        }

        return {
          ...state,
          [isOptional ? CONFIG_OPTIONAL_PRODUCTS_KEY : CONFIG_PRODUCTS_KEY]: products.map((entry, entryIndex) =>
            entryIndex === index ? updatedSvdConfig : entry,
          ),
        }
      }

      const prevSelectedOptions = productToReplace?.selectedOptions
      const updatedSelectedOptions = getInitialSelectedOptions(
        newSiblingProduct,
        prevSelectedOptions,
        false,
        false,
        prevSelectedOptions?.length,
      )
      const variants = productHelper.getVariantsForOptions(newSiblingProduct, updatedSelectedOptions)
      const pricing = productHelper.getProductPriceDetails(newSiblingProduct, updatedSelectedOptions)
      const isOutOfStock =
        variants.length === 1 && (purchaseInfo?.svidVsSvDetails[getVarientId(variants[0])]?.quantityAvailable || 0) <= 0

      const initialConfigForSiblingProduct = getProductInitialState({
        product: newSiblingProduct,
        searchTerm: searchParams,
        type: isOptional ? BUNDLE_PRODUCT_TYPE.OPTIONAL : BUNDLE_PRODUCT_TYPE.DEFAULT,
      })
      const updatedConfigForNewSiblingProduct: BundleProductConfig = {
        ...initialConfigForSiblingProduct,
        variants: variants,
        selectedOptions: updatedSelectedOptions,
        purchaseInfo,
        error: isOutOfStock ? OOS_ERROR_MESSAGE : null,
        salePrice: +pricing.formattedSalePrice,
        traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
        selection: prevProductSelection,
      }
      const updatedState = cloneDeep(state)
      updatedState[isOptional ? CONFIG_OPTIONAL_PRODUCTS_KEY : CONFIG_PRODUCTS_KEY][index] =
        updatedConfigForNewSiblingProduct
      return updatedState
    }

    case BundleActionType.UPDATE_PRICING_INFO_FOR_ALL: {
      const { purchaseInfo } = payload
      const updatedState = cloneDeep(state)
      updatedState.products = addPurchaseInfoToProductConfigs(state.products, purchaseInfo)
      updatedState.optionalProducts = addPurchaseInfoToProductConfigs(state.optionalProducts, purchaseInfo)
      return updatedState
    }

    default:
      return state
  }
}
